import {
  V3_SET_PRODUCT_CONFIG,
  V3_CLEAR_PRODUCT_CONFIG,
  V3_ADD_TO_CART,
  V3_DECLINE_RETOUCHING,
  V3_DECLINE_INTERSTITIAL_PRODUCT,
  V3_DECLINE_PRE_CART_OFFER_PRODUCT,
  V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
  V3_STORE_CART,
  V3_STORE_CART_ERROR,
  V3_STORE_CART_SUCCESS,
  V3_SUBMITTING_CART,
  V3_SUBMIT_CART_SUCCESS,
  V3_SUBMIT_CART_ERROR,
  V3_CLEAR_CART,
  V3_REMOVE_CART_ITEM,
  V3_RECORD_RETOUCHING_OFFERED,
  V3_LOG_RETOUCHING_OFFER,
  V3_RESOLVE_RETOUCHING_OFFER,
  V3_UPDATE_RETOUCHING_SELECTION,
  V3_CLEAR_RETOUCHING_SELECTION,
  V3_UPDATE_SMS_NUMBER_CONFIRMED,
  V3_POP_ITEM_FROM_CONFIG,
  V3_REMOVE_ITEM_FROM_CONFIG,
  V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
  V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
  V3_CHECKOUT_LOADING,
  V3_APPLY_DISCOUNT_CODE_TO_CART,
  V3_RESET_SUBMITTING_ORDER,
  V3_CONFIRM_SHIPPING_ADDRESS,
  V3_SET_ACCEPTED_PICS_PLUS,
  V3_SET_CART_UUID,
  V3_RESTORE_ABANDONED_CART_ATTRIBUTION,
  V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE,
  V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE,
  V3_SKIP_OPTIONAL_INCENTIVE,
  V3_ADD_INCENTIVE_TO_ORDER,
} from '../../actions/v3Order';
import { GET_SHOOT_DATA } from '../../actions/action_shoot';
import cartUtils from '../../utils/cart';
import {
  clearReAddedOptionalIncentiveIgnores,
  handleAddToCart,
} from './handleAddToCart';
import { clearProductScopedSelections, nextSlotState } from './slotState';

// Offer impressions kept per order; older entries drop off the front.
const RETOUCHING_OFFERS_KEPT = 20;

const INITIAL_STATE = {
  retouchingQuoteReviewedGeneration: null,
  retouchingCheckoutProtected: false,
  retouchingQuoteDigest: null,
  retouchingQuoteRequired: false,
  retouchingCheckoutApplicable: null,
  retouchingQuoteGeneration: 0,
  retouchingQuoteError: null,
  retouchingAttemptId: null,
  retouchingCheckoutRequest: null,
  retouchingCheckoutMessage: null,
  acceptedPicsPlus: false,
  abandonedCartNotificationId: null,
  cartTotals: {
    total: 0,
    shipping: 0,
    tax: 0,
    discount: 0,
    familyDiscount: 0,
    grandTotal: 0,
  },
  checkoutIsLoading: false,
  discountCodes: [],
  offerPicsPlus: false,
  optionalCartLevelIncentiveSkusToIgnore: [],
  optionalIncentiveProductIdsToIgnoreByOrder: {},
  optionalIncentiveToConfigure: null,
  isPicsPlusMember: false,
  picsPlusSubscriptionInfo: null,
  picsPlusExperiment: null,
  subscriberCount: 0,
  offeredPicsPlus: false,
  orders: {},
  productConfig: {},
  shippingAddressConfirmed: false,
  smsNumberConfirmed: false,
  storingCart: false,
  submittingOrder: false,
  uuid: null,
};

// Adds a product to the optional-incentive ignore lists for its scope.
// Order-level declines are recorded against the order by product id;
// cart-level ones against the BENEFIT by SKU, because the same benefit is a
// different row on every price list (ENG-2043 R3). A row with no sku falls
// back to its id. Unknown scope lands on both lists so redirect suppression
// errs on the side of not re-prompting a bonus the parent just declined.
const appendOptionalIncentiveIgnores = ({
  cartLevelSkus,
  idsByOrder,
  productId,
  sku,
  incentiveAppliesTo,
  orderKey,
}) => {
  const isCartLevel = incentiveAppliesTo === 'cart_level';
  const isOrderLevel = incentiveAppliesTo === 'order_level';
  let nextCartLevelSkus = cartLevelSkus;
  let nextIdsByOrder = idsByOrder;

  if (isCartLevel || !isOrderLevel) {
    nextCartLevelSkus = [
      ...new Set([...cartLevelSkus, sku != null ? sku : productId]),
    ];
  }

  if ((isOrderLevel || !isCartLevel) && orderKey) {
    nextIdsByOrder = {
      ...idsByOrder,
      [orderKey]: [...new Set([...(idsByOrder[orderKey] || []), productId])],
    };
  }

  return { nextCartLevelSkus, nextIdsByOrder };
};

// An optional-incentive decline lives exactly as long as the order it was
// recorded against. When a removal drops an order out of `orders` (its last
// item went away), every decline recorded under that orderKey goes with it, so
// re-adding items under the same key (orderKey is `<eventId>-<studentId>`, so
// it comes back identical) prompts for the bonus again instead of being
// silently suppressed by a stale entry. When nothing is left in the cart at
// all, the cart-level declines reset too, matching V3_CLEAR_CART.
//
// Boundary, deliberately: an order that SURVIVES the removal keeps its
// decline, even if it drops back below the bonus threshold. Only emptying an
// order forgets what the parent declined for it.
const pruneOptionalIncentiveIgnoresForDroppedOrders = ({
  cartLevelSkus,
  idsByOrder,
  optionalIncentiveToConfigure,
  previousOrders,
  nextOrders,
}) => {
  const remainingOrders = nextOrders || {};
  const droppedOrderKeys = Object.keys(previousOrders || {}).filter(
    (key) => !remainingOrders[key],
  );

  if (droppedOrderKeys.length === 0) {
    return {
      nextCartLevelSkus: cartLevelSkus,
      nextIdsByOrder: idsByOrder,
      nextOptionalIncentiveToConfigure: optionalIncentiveToConfigure,
    };
  }

  const cartIsEmpty = Object.keys(remainingOrders).length === 0;
  let nextIdsByOrder;

  if (cartIsEmpty) {
    nextIdsByOrder = {};
  } else {
    // Copy before deleting: this map may still be the one held by the previous
    // state, which must not be mutated.
    nextIdsByOrder = { ...idsByOrder };
    droppedOrderKeys.forEach((key) => {
      delete nextIdsByOrder[key];
    });
  }

  const pendingOrderKey = optionalIncentiveToConfigure
    ? optionalIncentiveToConfigure.orderKey
    : null;
  const nextOptionalIncentiveToConfigure =
    cartIsEmpty || droppedOrderKeys.includes(pendingOrderKey)
      ? null
      : optionalIncentiveToConfigure;

  return {
    nextCartLevelSkus: cartIsEmpty ? [] : cartLevelSkus,
    nextIdsByOrder,
    nextOptionalIncentiveToConfigure,
  };
};

function reduceOrder(state = INITIAL_STATE, action) {
  Object.freeze(state);

  let existing;

  switch (action.type) {
    case 'V3_APPLY_RETOUCHING_QUOTE': {
      if (
        action.payload.generation !== (state.retouchingQuoteGeneration || 0) ||
        state.retouchingCheckoutProtected ||
        state.retouchingCheckoutRequest
      )
        return state;
      try {
        if (action.payload.required === true && !action.payload.digest)
          throw new Error('Missing quote digest');
        const contexts = action.payload.productContexts;
        const orders = { ...state.orders };
        Object.entries(state.orders).forEach(([key, order]) => {
          const context = contexts?.[key];
          if (
            !context ||
            typeof context.retouchingRequiresImageSelection !== 'boolean' ||
            !Array.isArray(context.retouchingProducts)
          )
            throw new Error('Missing destination context');
          if (
            context.retouchingProducts.some(
              (product) => !cartUtils.validRetouchingPrice(product),
            )
          )
            throw new Error('Invalid pricing context');
          const cartItems = (order.cartItems || []).map((item) => {
            if (!item.isRetouching) return item;
            if (
              item.retouchingPricingMode === 'per_image' &&
              !cartUtils.validRetouchingLinePrice(item)
            )
              throw new Error('Invalid retouching selection metadata');
            const product = context.retouchingProducts.find(
              (product) => String(product.id) === String(item.productId),
            );
            if (!product)
              throw new Error('Retouch product no longer available');
            const selectedProductOptions = (
              item.selectedProductOptions ||
              (item.productOption
                ? [
                    {
                      optionSetKey: 'legacy',
                      optionId: item.productOption.id,
                      optionKey: item.productOption.key,
                    },
                  ]
                : [])
            ).map((selection) => {
              const set = (product.product_option_sets || []).find(
                (set) => set.key === selection.optionSetKey,
              );
              const options =
                set?.product_options ||
                (selection.optionSetKey === 'legacy'
                  ? product.product_options
                  : []) ||
                [];
              const option = options.find(
                (option) =>
                  String(option.id) === String(selection.optionId) ||
                  (option.key && option.key === selection.optionKey),
              );
              if (
                !option ||
                !Number.isInteger(option.price) ||
                option.price < 0 ||
                option.price > 2147483647
              )
                throw new Error('Selected option needs review');
              return {
                ...selection,
                optionId: option.id,
                optionKey: option.key,
                optionName: option.name,
                optionPrice: option.price,
              };
            });
            const explicit = cartUtils.canSelectRetouchingForOrder({
              order,
              requiresImageSelection: context.retouchingRequiresImageSelection,
            });
            const next = {
              ...item,
              price: product.price,
              retouchingTieredUnitPricesCents: product.tiered_unit_prices_cents,
              selectedProductOptions,
            };
            if (explicit && (item.poseIds || []).length)
              next.retouchingPricingMode = 'per_image';
            else delete next.retouchingPricingMode;
            return next;
          });
          orders[key] = {
            ...order,
            cartItems,
            productContext: { ...order.productContext, ...context },
            additionalData: {
              ...order.additionalData,
              retouchingRequiresImageSelection:
                context.retouchingRequiresImageSelection,
            },
          };
        });
        return {
          ...state,
          orders,
          retouchingQuoteReviewedGeneration: action.payload.generation,
          retouchingQuoteDigest: action.payload.digest ?? null,
          retouchingQuoteRequired: action.payload.required === true,
          retouchingCheckoutApplicable:
            action.payload.checkoutApplicable ?? null,
          cartTotals: action.payload.cartTotals,
          retouchingQuoteError: null,
          storingCart: false,
        };
      } catch {
        return {
          ...state,
          retouchingQuoteDigest: null,
          retouchingCheckoutApplicable: null,
          retouchingQuoteReviewedGeneration: null,
          retouchingQuoteError:
            'Retouching pricing needs review. Please refresh your cart or contact support.',
          storingCart: false,
        };
      }
    }
    case 'V3_RETOUCHING_CHECKOUT_PENDING':
      return {
        ...state,
        retouchingCheckoutProtected: true,
        retouchingCheckoutRequest:
          action.payload.request ?? state.retouchingCheckoutRequest,
        retouchingAttemptId:
          action.payload.attemptId ?? state.retouchingAttemptId,
        retouchingCheckoutMessage:
          action.payload.message ||
          'Payment is protected. Retry the original checkout or return from payment to cancel it.',
      };
    case 'V3_RETOUCHING_CHECKOUT_RETIRED':
      if (
        state.retouchingAttemptId &&
        state.retouchingAttemptId !== action.payload.attemptId
      )
        return state;
      return {
        ...state,
        retouchingCheckoutProtected: false,
        retouchingCheckoutRequest: null,
        retouchingAttemptId: null,
        retouchingQuoteDigest: null,
        retouchingCheckoutApplicable: null,
        retouchingQuoteReviewedGeneration: null,
        retouchingQuoteGeneration: (state.retouchingQuoteGeneration || 0) + 1,
        retouchingCheckoutMessage:
          'Checkout cancelled. Please review fresh prices before placing your order.',
      };
    case 'V3_RETOUCHING_CHECKOUT_NOT_ACCEPTED':
      return {
        ...state,
        retouchingCheckoutMessage: null,
        retouchingCheckoutProtected: false,
        retouchingCheckoutRequest: null,
        retouchingAttemptId: null,
      };
    case V3_ADD_TO_CART:
      return handleAddToCart(state, action.payload);
    case 'V3_DECLINE_PERSONALIZATION': {
      const orderKey = action.payload.orderKey;
      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            personalization: {
              ...state.orders[orderKey].personalization,
              shouldPrompt: false,
            },
          },
        },
      };
    }

    case 'V3_SUBMIT_PERSONALIZATION': {
      const orderKey = action.payload.orderKey;
      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            personalization: {
              value: action.payload.value,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS: {
      const orderKey = action.payload.orderKey;
      const submittedPersonalizationValue = action.payload.personalizationValue;

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            cartItems: state.orders[orderKey].cartItems.map((item) => {
              if (
                item.personalizationStatus === 'personalization_optional' ||
                item.personalizationStatus === 'personalization_required'
              ) {
                return {
                  ...item,
                  personalizationValue: submittedPersonalizationValue,
                };
              }

              // Check childProducts
              if (item.children) {
                const updatedChildProducts = item.children.map((childItem) => {
                  if (
                    childItem.personalizationStatus ===
                      'personalization_optional' ||
                    childItem.personalizationStatus ===
                      'personalization_required'
                  ) {
                    return {
                      ...childItem,
                      personalizationValue: submittedPersonalizationValue,
                    };
                  }
                  return childItem;
                });
                return { ...item, children: updatedChildProducts };
              }

              return item;
            }),
            personalization: {
              value: submittedPersonalizationValue,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS: {
      const orderKey = action.payload.orderKey;

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...state.orders[orderKey],
            cartItems: state.orders[orderKey].cartItems.map((item) => {
              if (
                item.personalizationStatus === 'personalization_optional' ||
                item.personalizationStatus === 'personalization_required'
              ) {
                return {
                  ...item,
                  personalizationValue: null,
                };
              }

              // Check childProducts
              if (item.children) {
                const updatedChildProducts = item.children.map((childItem) => {
                  if (
                    childItem.personalizationStatus ===
                      'personalization_optional' ||
                    childItem.personalizationStatus ===
                      'personalization_required'
                  ) {
                    return {
                      ...childItem,
                      personalizationValue: null,
                    };
                  }
                  return childItem;
                });
                return { ...item, children: updatedChildProducts };
              }

              return item;
            }),
            personalization: {
              value: null,
              shouldPrompt: false,
            },
          },
        },
      };
    }
    case V3_DECLINE_RETOUCHING: {
      const { orderKey } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const updatedOrder = {
        ...order,
        retouching: {
          ...order.retouching,
          shouldPrompt: false,
        },
      };

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: updatedOrder,
        },
      };
    }
    // The photos a retouching screen has shown for this order. The set only
    // grows, and a cart persisted before it existed reads as an empty set.
    // Showing any screen also spends the post-add prompt, which no case ever
    // sets back to true.
    case V3_RECORD_RETOUCHING_OFFERED: {
      const { orderKey, poseIds } = action.payload;
      const order = state.orders[orderKey];
      if (!order) return state;

      const offeredPoseIds = [
        ...new Set([...(order.retouching?.offeredPoseIds || []), ...poseIds]),
      ].sort((a, b) => a - b);

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...order,
            retouching: {
              ...order.retouching,
              shouldPrompt: false,
              offeredPoseIds,
            },
          },
        },
      };
    }
    case V3_LOG_RETOUCHING_OFFER: {
      const { orderKey, entry } = action.payload;
      const order = state.orders[orderKey];
      if (!order) return state;

      const offers = [
        ...(order.retouching?.offers || []),
        { ...entry, outcome: 'left' },
      ].slice(-RETOUCHING_OFFERS_KEPT);

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...order,
            retouching: { ...order.retouching, offers },
          },
        },
      };
    }
    case V3_RESOLVE_RETOUCHING_OFFER: {
      const { orderKey, outcome } = action.payload;
      const order = state.orders[orderKey];
      if (!order) return state;

      const offers = order.retouching?.offers || [];
      let index = -1;
      for (let i = offers.length - 1; i >= 0; i -= 1) {
        if (offers[i]?.outcome === 'left') {
          index = i;
          break;
        }
      }
      if (index === -1) return state;

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...order,
            retouching: {
              ...order.retouching,
              offers: offers.map((offer, i) =>
                i === index ? { ...offer, outcome } : offer,
              ),
            },
          },
        },
      };
    }
    case V3_DECLINE_INTERSTITIAL_PRODUCT: {
      const { orderKey } = action.payload;
      const { productId } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const promptedProducts = order.interstitialProductsPrompted || [];
      const productAlreadyExists = promptedProducts.includes(productId);

      const updatedOrder = {
        ...order,
        interstitialProductsPrompted: productAlreadyExists
          ? promptedProducts
          : [...promptedProducts, productId],
      };

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: updatedOrder,
        },
      };
    }
    case V3_DECLINE_PRE_CART_OFFER_PRODUCT:
    case V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED: {
      {
        const { orderKey } = action.payload;
        const { productId } = action.payload;
        const order = state.orders[orderKey];

        if (!order) {
          return state;
        }

        const promptedProducts = order.preCartOfferProductsPrompted || [];
        const productAlreadyExists = promptedProducts.includes(productId);

        const updatedOrder = {
          ...order,
          preCartOfferProductsPrompted: productAlreadyExists
            ? promptedProducts
            : [...promptedProducts, productId],
        };

        return {
          ...state,
          orders: {
            ...state.orders,
            [orderKey]: updatedOrder,
          },
        };
      }
    }
    case V3_SUBMIT_CART_ERROR: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_SUBMIT_CART_SUCCESS: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_SUBMITTING_CART: {
      return {
        ...state,
        submittingOrder: true,
      };
    }
    case V3_STORE_CART: {
      return {
        ...state,
        storingCart: true,
      };
    }
    case V3_STORE_CART_ERROR: {
      return {
        ...state,
        storingCart: false,
      };
    }
    case V3_STORE_CART_SUCCESS: {
      return {
        ...state,
        storingCart: false,
        cartTotals: action.payload.cartTotals,
        offerPicsPlus: action.payload.offerPicsPlus,
        isPicsPlusMember: action.payload.isPicsPlusMember || false,
        picsPlusSubscriptionInfo:
          action.payload.picsPlusSubscriptionInfo || null,
        picsPlusExperiment: action.payload.picsPlusExperiment || null,
        subscriberCount: action.payload.subscriberCount || 0,
      };
    }
    case V3_SET_PRODUCT_CONFIG: {
      const {
        index,
        isPrepay,
        itemToAdd,
        prepayRequiresSameValuePersonalization,
      } = action.payload;

      const itemToAddKey = Object.keys(itemToAdd)[0];

      let productConfig;

      // If the shoot is prepay and prepay requires the same value for personalization
      // across all cart items, ensure the config is updated with the same value for each
      // child product's personalization.
      if (
        itemToAddKey?.toLowerCase() === 'personalization' &&
        isPrepay &&
        prepayRequiresSameValuePersonalization &&
        itemToAdd.personalization !== undefined &&
        itemToAdd.personalization !== null &&
        itemToAdd.personalization !== ''
      ) {
        productConfig = Object.entries(state.productConfig).reduce(
          (newProductConfig, [key, value]) => {
            return {
              ...newProductConfig,
              [key]: {
                ...value,
                personalization:
                  value.personalization !== undefined &&
                  value.personalization !== null
                    ? itemToAdd.personalization
                    : value.personalization,
              },
            };
          },
          {},
        );
      } else {
        // If the conditions aren't met, just use the original productConfig
        productConfig = state.productConfig;
      }
      // nextSlotState is shared with addToConfig, which has to route on the same
      // answer this stores — see slotState.js for why they cannot derive it apart.
      return {
        ...state,
        productConfig: {
          ...productConfig,
          [index]: nextSlotState(productConfig[index], itemToAdd),
        },
      };
    }
    case V3_POP_ITEM_FROM_CONFIG: {
      const { index: popIndex, section } = action.payload;
      const slot = state.productConfig[popIndex];
      if (!slot || !slot[section]) return state;

      // Removing the PRODUCT takes its scoped selections with it. This is the
      // sequence the app actually performs: ConfigScreen's mount effect pops
      // `.product` when the customer reopens a configured slot's product screen
      // (configScreen.tsx), and only then do they pick the replacement. Leaving the
      // old background behind here is what put one on a child with no background
      // slot — counted by the completion gates, where it can even stand in for a
      // background another child still owes, and shipped to the lab by
      // buildCartObject.
      //
      // Re-picking the SAME product also loses these, which is correct: the flow
      // re-walks its steps after a product pick, so anything still needed is asked
      // for again.
      const remaining =
        section === 'product'
          ? clearProductScopedSelections(slot)
          : { ...slot };
      delete remaining[section];

      return {
        ...state,
        productConfig: { ...state.productConfig, [popIndex]: remaining },
      };
    }
    case V3_REMOVE_ITEM_FROM_CONFIG:
      existing = { ...state };
      const keys = Object.keys(
        existing.productConfig[action.payload.index],
      ).length;
      Object.keys(existing.productConfig).forEach((key, index) => {
        if (index < action.payload.index) return;
        else if (existing.productConfig[index + 1])
          existing.productConfig[index] = existing.productConfig[index + 1];
        else delete existing.productConfig[index];
      });
      action.payload.history.go(-keys);
      return existing;
    case V3_CLEAR_PRODUCT_CONFIG:
      let productConfig;

      if (action.payload) {
        productConfig = {
          ...state.productConfig,
          [action.payload]: [],
        };
      } else {
        productConfig = {};
      }

      return {
        ...state,
        productConfig,
      };
    case V3_CLEAR_CART: {
      return {
        ...state,
        cartTotals: {},
        discountCodes: [],
        optionalCartLevelIncentiveSkusToIgnore: [],
        optionalIncentiveProductIdsToIgnoreByOrder: {},
        optionalIncentiveToConfigure: null,
        orders: {},
        shippingAddressConfirmed: false,
        abandonedCartNotificationId: null,
        uuid: null,
      };
    }
    case V3_SET_CART_UUID: {
      if (state.uuid) return state;
      return {
        ...state,
        uuid: action.payload,
      };
    }
    case V3_RESTORE_ABANDONED_CART_ATTRIBUTION: {
      if (!action.payload.orders || !action.payload.uuid) return state;
      return {
        ...INITIAL_STATE,
        orders: action.payload.orders,
        discountCodes: action.payload.discountCodes || [],
        uuid: action.payload.uuid,
        abandonedCartNotificationId: action.payload.abandonedCartNotificationId,
      };
    }
    case GET_SHOOT_DATA: {
      // Refresh the cart-level incentive catalog stashed on this shoot's
      // orders (ENG-2965). The stash is persisted with the cart, so without
      // this a returning parent could be offered an incentive whose
      // threshold was raised or that was deactivated since the capture —
      // the freshly served catalog is authoritative whenever a shoot's
      // data loads.
      const shootData = action.payload?.data;
      const loadedEventId = shootData?.shoot?.event_id || shootData?.shoot?.id;
      if (!loadedEventId) return state;

      const tagRestriction = (product) => ({
        ...product,
        block_international_shipping:
          shootData.shoot?.block_international_shipping === true,
      });
      const freshCatalog = (shootData.incentive_products || [])
        .filter((product) => product.incentive_applies_to === 'cart_level')
        .map(tagRestriction);
      // Order-level rows are stashed separately (ENG-2043 R2) so an order
      // from a shoot that is not the active one can still be judged against
      // its own catalog.
      const freshOrderLevelCatalog = (shootData.incentive_products || [])
        .filter((product) => product.incentive_applies_to !== 'cart_level')
        .map(tagRestriction);
      // The owning company's opt-in flag, refreshed with the catalog: an
      // order must never keep a decline the studio no longer allows.
      const optionalIncentivesEnabled =
        shootData.shoot?.optional_incentives_enabled === true;

      const keyPrefix = `${loadedEventId}-`;
      // The catalog is gallery-specific within one shoot (buyer vs
      // non-buyer reorder price lists), so a same-shoot sibling order
      // backed by a DIFFERENT gallery must keep its own stash. Null on
      // either side (DP2 flows, legacy orders) keeps the event-level
      // refresh.
      const loadedGalleryId = shootData.pics_gallery_id ?? null;
      const orderKeysForShoot = Object.keys(state.orders).filter((key) => {
        if (!key.startsWith(keyPrefix)) return false;
        const orderGalleryId =
          state.orders[key].additionalData?.gallery_id ?? null;
        return (
          orderGalleryId === null ||
          loadedGalleryId === null ||
          orderGalleryId === loadedGalleryId
        );
      });
      if (orderKeysForShoot.length === 0) return state;

      const refreshedOrders = { ...state.orders };
      orderKeysForShoot.forEach((key) => {
        refreshedOrders[key] = {
          ...refreshedOrders[key],
          incentiveProducts: freshCatalog,
          orderLevelIncentiveProducts: freshOrderLevelCatalog,
          optionalIncentivesEnabled,
        };
      });

      // A bonus this company no longer lets parents decline must be
      // re-prompted, not silently suppressed by a decline recorded while it
      // was optional — so the refreshed orders' declines go with the flag
      // (ENG-2043 R2). Cart-level SKUs offered by this shoot's own catalog go
      // too: the shared-SKU rule would refuse to honour them anyway.
      if (optionalIncentivesEnabled) {
        return {
          ...state,
          orders: refreshedOrders,
        };
      }

      const nextIdsByOrder = {
        ...(state.optionalIncentiveProductIdsToIgnoreByOrder || {}),
      };
      orderKeysForShoot.forEach((key) => {
        delete nextIdsByOrder[key];
      });

      const mandatorySkus = new Set(
        freshCatalog.map((product) =>
          product.sku != null ? product.sku : product.id,
        ),
      );

      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore: (
          state.optionalCartLevelIncentiveSkusToIgnore || []
        ).filter((sku) => !mandatorySkus.has(sku)),
        optionalIncentiveProductIdsToIgnoreByOrder: nextIdsByOrder,
        orders: refreshedOrders,
      };
    }
    case V3_REMOVE_CART_ITEM: {
      const {
        orderKey,
        cartItemId,
        voucherTotalCents = 0,
        hasVouchers = false,
        currentEventId,
        currentIncentiveProducts = [],
        currentRetouchingRequiresImageSelection,
      } = action.payload;
      const studentOrder = state.orders[orderKey];

      if (!studentOrder) {
        return state; // No changes, orderKey not found.
      }

      const cartItemToRemove = studentOrder.cartItems.find(
        (item) => item.id === cartItemId,
      );

      if (!cartItemToRemove) {
        return state; // No changes, cartItemId not found.
      }

      let newOptionalCartLevelIncentiveSkusToIgnore =
        state.optionalCartLevelIncentiveSkusToIgnore || [];
      let newOptionalIncentiveProductIdsToIgnoreByOrder =
        state.optionalIncentiveProductIdsToIgnoreByOrder || {};

      // A removal the PARENT made is a decline; one the app made for them
      // (the cart's restricted-shipping cleanup) is not, and must never be
      // recorded as one — see ENG-2043 R6.
      const recordDecline = action.payload.recordDecline !== false;

      if (cartItemToRemove.isIncentive && recordDecline) {
        ({
          nextCartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
          nextIdsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
        } = appendOptionalIncentiveIgnores({
          cartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
          idsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
          productId: cartItemToRemove.productId,
          sku: cartItemToRemove.sku ?? null,
          incentiveAppliesTo:
            cartItemToRemove.incentiveAppliesTo ||
            cartItemToRemove.incentive_applies_to,
          orderKey,
        }));
      }

      let updatedCartItems = studentOrder.cartItems;

      if (cartItemToRemove.isPersonalization) {
        // Iterate through all other items in the order and set personalizationValue to null,
        // also handle the child items if they exist.
        updatedCartItems = studentOrder.cartItems.map((item) => ({
          ...item,
          personalizationValue: null,
          children: item.children
            ? item.children.map((childItem) => ({
                ...childItem,
                personalizationValue: null,
              }))
            : item.children,
        }));
      }

      const newCartItems = updatedCartItems.filter(
        (item) => item.id !== cartItemId,
      );

      // Remove the productId from interstitialProductsPrompted if it exists.
      const newInterstitialProductsPrompted =
        studentOrder.interstitialProductsPrompted.filter(
          (id) => id !== cartItemToRemove.productId,
        );

      // Drop retouching selections that referenced images no longer in the
      // cart (retouching items pruned to zero selections are removed).
      //
      // Use the live policy only for the event currently loaded in Redux, so
      // activation and rollback agree with the UI and backend. A different
      // event keeps its own persisted snapshot and cannot borrow global state.
      const requiresImageSelection = (order, key) => {
        const orderEventId = order.event?.id ?? key.split('-')[0];
        const belongsToCurrentEvent =
          currentEventId != null &&
          String(orderEventId) === String(currentEventId);
        return belongsToCurrentEvent
          ? currentRetouchingRequiresImageSelection === true
          : order?.additionalData?.retouchingRequiresImageSelection === true;
      };
      const prunedCartItems = cartUtils.pruneRetouchingSelections(
        newCartItems,
        requiresImageSelection(studentOrder, orderKey),
      );

      const prunedCartItemIds = prunedCartItems.map((item) => item.id);

      // Create new studentOrder with updated cartItemIds and cartItems.
      const newStudentOrder = {
        ...studentOrder,
        cartItemIds: prunedCartItemIds,
        cartItems: prunedCartItems,
        interstitialProductsPrompted: newInterstitialProductsPrompted,
      };

      const editedOrders = { ...state.orders, [orderKey]: newStudentOrder };
      let newOrders = cartUtils.ordersWithUnearnedIncentivesRemoved(
        editedOrders,
        orderKey,
        action.payload.voucherTotalCents,
      );

      // A bonus removed as unearned may have held the only copy of a photo
      // selected for retouching, in this order or a sibling the sweep
      // touched; checkout refuses a selection the cart no longer buys. Prune
      // again, and re-qualify any order that lost retouching, since that
      // spend may have been what earned another bonus. Each pass only
      // removes items, so this settles.
      let pruned = true;
      while (pruned) {
        pruned = false;
        for (const [key, order] of Object.entries(newOrders)) {
          const cartItems = order.cartItems || [];
          const kept = cartUtils.pruneRetouchingSelections(
            cartItems,
            requiresImageSelection(order, key),
          );
          if (
            kept.length === cartItems.length &&
            kept.every((item, index) => item === cartItems[index])
          )
            continue;
          pruned = true;
          newOrders = cartUtils.ordersWithUnearnedIncentivesRemoved(
            {
              ...newOrders,
              [key]: {
                ...order,
                cartItemIds: kept.map((item) => item.id),
                cartItems: kept,
              },
            },
            key,
            action.payload.voucherTotalCents,
          );
          break;
        }
      }

      // The one-way prompt belongs to image selection, whose pre-cart offer
      // covers photos added later. A studio that does not select images keeps
      // the legacy flat prompt unchanged: once the order has no retouching
      // left, the flat screen comes back on the next add.
      const remainingOrder = newOrders[orderKey];
      if (
        !requiresImageSelection(studentOrder, orderKey) &&
        remainingOrder?.retouching?.shouldPrompt === false &&
        cartUtils.orderHasNoRetouching(remainingOrder.cartItems)
      ) {
        newOrders = {
          ...newOrders,
          [orderKey]: {
            ...remainingOrder,
            retouching: { ...remainingOrder.retouching, shouldPrompt: true },
          },
        };
      }

      // Declines recorded against an order that no longer exists must not
      // outlive it. Derived from the final `newOrders` map — which the
      // re-qualification above may have emptied, along with any sibling
      // order it swept — rather than from a single deletion site, so it
      // holds however the order set was computed.
      const {
        nextCartLevelSkus: prunedCartLevelIncentiveSkusToIgnore,
        nextIdsByOrder: prunedIncentiveIdsToIgnoreByOrder,
        nextOptionalIncentiveToConfigure,
      } = pruneOptionalIncentiveIgnoresForDroppedOrders({
        cartLevelSkus: newOptionalCartLevelIncentiveSkusToIgnore,
        idsByOrder: newOptionalIncentiveProductIdsToIgnoreByOrder,
        optionalIncentiveToConfigure: state.optionalIncentiveToConfigure,
        previousOrders: state.orders,
        nextOrders: newOrders,
      });

      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore:
          prunedCartLevelIncentiveSkusToIgnore,
        optionalIncentiveProductIdsToIgnoreByOrder:
          prunedIncentiveIdsToIgnoreByOrder,
        optionalIncentiveToConfigure: nextOptionalIncentiveToConfigure,
        orders: newOrders,
      };
    }
    // A bonus put straight back into the order that earned it, with no trip
    // through the configurator (ENG-2043 re-add Tier 1). The cart object was
    // built by the addIncentiveToOrder thunk from the TARGET order, so this
    // case only has to place it: variant id against that order's own items,
    // and the decline it answers cleared under that order's key.
    case V3_ADD_INCENTIVE_TO_ORDER: {
      const { orderKey, cartObject, productId, sku } = action.payload;
      const order = state.orders[orderKey];

      if (!order) {
        return state;
      }

      const cartItems = order.cartItems || [];
      const newCartItem = cartUtils.createNewCartItem(
        cartObject,
        cartUtils.getNextVariantId(cartItems, cartObject.productId),
      );

      return {
        ...state,
        ...clearReAddedOptionalIncentiveIgnores(state, {
          productId,
          sku,
          orderKey,
        }),
        optionalIncentiveToConfigure:
          cartUtils.optionalIncentiveMarkerMatchesProduct(
            state.optionalIncentiveToConfigure,
            {
              productId,
              sku,
              incentiveAppliesTo: cartObject.incentiveAppliesTo,
            },
          )
            ? null
            : state.optionalIncentiveToConfigure,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...order,
            cartItemIds: [...(order.cartItemIds || []), newCartItem.id],
            cartItems: [...cartItems, newCartItem],
          },
        },
      };
    }
    case V3_UPDATE_RETOUCHING_SELECTION: {
      const {
        orderKey,
        cartItemId,
        poses,
        unitPrice,
        notes,
        optionFields,
        sources,
      } = action.payload;
      const studentOrder = state.orders[orderKey];

      if (!studentOrder) {
        return state; // No changes, orderKey not found.
      }

      // Pricing::RetouchingLinePricer counts poseIds.compact.uniq, so a
      // duplicated id would display above what we actually charge.
      const seenIds = new Set();
      const nextPoses = (poses || []).filter((pose) => {
        if (!pose || pose.id == null || seenIds.has(pose.id)) return false;
        seenIds.add(pose.id);
        return true;
      });

      // Emptying a line is a removal, not an edit — V3_REMOVE_CART_ITEM owns
      // that path because it also prunes incentives.
      if (nextPoses.length === 0) {
        return state;
      }

      let updated = false;

      const nextCartItems = (studentOrder.cartItems || []).map((item) => {
        if (item.id !== cartItemId || !item.isRetouching) return item;

        updated = true;

        // First touch wins: a photo that already has a source keeps it, a
        // photo new to the line takes the one this update carries, and a
        // photo leaving the line takes its entry with it.
        const keptIds = new Set(nextPoses.map((pose) => String(pose.id)));
        const nextSources = {};
        [sources || {}, item.retouchingSources || {}].forEach((map) => {
          Object.entries(map).forEach(([poseId, source]) => {
            if (keptIds.has(String(poseId)) && source)
              nextSources[poseId] = source;
          });
        });

        // poses, poseIds and quantity move together — the same invariant
        // pruneRetouchingSelections maintains on the remove side. The server
        // prices off poseIds when present and off quantity otherwise, so they
        // must never disagree.
        //
        // selectedCrop is derived from the poses by createNewCartItem, so it
        // has to be re-derived here too. BuildOrderFromCart falls back to the
        // cart-level crop whenever a line is down to one image, so a stale
        // value would tag the surviving image with a deselected image's crop.
        const nextItem = {
          ...item,
          retouchingSources: nextSources,
          ...(optionFields
            ? {
                selectedProductOptions:
                  optionFields.selectedProductOptions ?? [],
                productOption: optionFields.productOption,
              }
            : {}),
          poses: nextPoses,
          poseIds: nextPoses.map((pose) => pose.id),
          quantity: nextPoses.length,
          price: unitPrice ?? item.price,
          retouchingNotes:
            notes === undefined
              ? item.retouchingNotes
              : Object.keys(notes).length
              ? notes
              : undefined,
          retouchingPricingMode: 'per_image',
          retouchingTieredUnitPricesCents:
            studentOrder.productContext?.retouchingProducts?.find(
              (product) => String(product.id) === String(item.productId),
            )?.tiered_unit_prices_cents ??
            item.retouchingTieredUnitPricesCents ??
            [],
          selectedCrop: nextPoses.find((pose) => !!pose.selected_crop)
            ?.selected_crop,
        };
        if (!Object.keys(nextSources).length) delete nextItem.retouchingSources;
        return nextItem;
      });

      if (!updated) {
        return state; // No changes, cartItemId not found on a retouching line.
      }

      return {
        ...state,
        orders: {
          ...state.orders,
          [orderKey]: {
            ...studentOrder,
            cartItems: nextCartItems,
          },
        },
      };
    }
    case V3_CLEAR_RETOUCHING_SELECTION: {
      const { orderKey, cartItemId } = action.payload;
      const studentOrder = state.orders[orderKey];
      if (!studentOrder) return state;

      const item = (studentOrder.cartItems || []).find(
        (cartItem) => cartItem.id === cartItemId,
      );
      if (!item?.isRetouching) return state;

      const cartItems = studentOrder.cartItems.filter(
        (cartItem) => cartItem.id !== cartItemId,
      );
      const orders = {
        ...state.orders,
        [orderKey]: {
          ...studentOrder,
          cartItems,
          cartItemIds: cartItems.map((cartItem) => cartItem.id),
        },
      };

      if (cartItems.length === 0) delete orders[orderKey];

      return { ...state, orders };
    }
    case V3_SET_OPTIONAL_INCENTIVE_TO_CONFIGURE: {
      const { productId, sku, orderKey } = action.payload;
      return {
        ...state,
        optionalIncentiveToConfigure: { productId, sku: sku ?? null, orderKey },
      };
    }
    case V3_SKIP_OPTIONAL_INCENTIVE: {
      const { productId, sku, incentiveAppliesTo, orderKey } = action.payload;
      const { nextCartLevelSkus, nextIdsByOrder } =
        appendOptionalIncentiveIgnores({
          cartLevelSkus: state.optionalCartLevelIncentiveSkusToIgnore || [],
          idsByOrder: state.optionalIncentiveProductIdsToIgnoreByOrder || {},
          productId,
          sku: sku ?? null,
          incentiveAppliesTo,
          orderKey,
        });
      return {
        ...state,
        optionalCartLevelIncentiveSkusToIgnore: nextCartLevelSkus,
        optionalIncentiveProductIdsToIgnoreByOrder: nextIdsByOrder,
      };
    }
    case V3_CLEAR_OPTIONAL_INCENTIVE_TO_CONFIGURE:
      return { ...state, optionalIncentiveToConfigure: null };
    case V3_UPDATE_SMS_NUMBER_CONFIRMED: {
      return {
        ...state,
        smsNumberConfirmed: action.payload,
      };
    }
    case V3_CHECKOUT_LOADING: {
      const { checkoutIsLoading } = action.payload;

      return {
        ...state,
        checkoutIsLoading,
      };
    }
    case V3_APPLY_DISCOUNT_CODE_TO_CART: {
      return cartUtils.applyDiscountCodeToCart(state, action.payload.data);
    }
    case V3_RESET_SUBMITTING_ORDER: {
      return {
        ...state,
        submittingOrder: false,
      };
    }
    case V3_CONFIRM_SHIPPING_ADDRESS: {
      return {
        ...state,
        shippingAddressConfirmed: true,
      };
    }
    case V3_SET_ACCEPTED_PICS_PLUS: {
      const { accepted } = action.payload;
      return {
        ...state,
        acceptedPicsPlus: accepted,
        offeredPicsPlus: true,
      };
    }
    default:
      return state;
  }
}

const quoteInputActions = new Set([
  'ADD_VOUCHER_TO_CART',
  'UPDATE_CART_VOUCHER_ITEM',
  'REMOVE_CART_VOUCHER_ITEM',
  'ACCEPT_VOUCHER_TERMS',
  'CLEAR_CART_VOUCHER_ITEMS',
  'FETCH_CART_VOUCHER_ITEMS',
  'UPDATE_PARENT_ADDRESS_SUCCESS',
  'UPDATE_PARENT_SUCCESS',
  V3_CONFIRM_SHIPPING_ADDRESS,
  V3_SET_ACCEPTED_PICS_PLUS,
]);
// Offer bookkeeping lives in the cart JSON but never prices anything, so it
// must not throw away a reviewed retouching quote.
const retouchingOfferActions = new Set([
  V3_RECORD_RETOUCHING_OFFERED,
  V3_LOG_RETOUCHING_OFFER,
  V3_RESOLVE_RETOUCHING_OFFER,
]);
const cartMutationActions = new Set([
  V3_ADD_TO_CART,
  V3_REMOVE_CART_ITEM,
  V3_UPDATE_RETOUCHING_SELECTION,
  V3_CLEAR_RETOUCHING_SELECTION,
  V3_CLEAR_CART,
  V3_APPLY_DISCOUNT_CODE_TO_CART,
  V3_RESTORE_ABANDONED_CART_ATTRIBUTION,
  V3_ADD_INCENTIVE_TO_ORDER,
  'V3_SUBMIT_PERSONALIZATION',
  'V3_DECLINE_PERSONALIZATION',
  V3_SET_PERSONALIZATION_ON_ALL_ELIGIBLE_CART_ITEMS,
  V3_SKIP_PERSONALIZATION_ON_ALL_CART_ITEMS,
]);
export default function v3OrderReducer(state = INITIAL_STATE, action) {
  if (action.type === 'persist/REHYDRATE' && action.payload?.v3Order) {
    const saved = action.payload.v3Order;
    const invalidExplicitLine = Object.values(saved.orders || {}).some(
      (order) =>
        (order.cartItems || []).some(
          (item) =>
            item.retouchingPricingMode === 'per_image' &&
            !cartUtils.validRetouchingLinePrice(item),
        ),
    );
    return {
      ...INITIAL_STATE,
      ...saved,
      retouchingQuoteDigest: null,
      retouchingCheckoutApplicable: null,
      retouchingQuoteReviewedGeneration: null,
      retouchingQuoteGeneration: (saved.retouchingQuoteGeneration || 0) + 1,
      retouchingQuoteError: invalidExplicitLine
        ? 'Retouching pricing needs review. Please refresh your cart or contact support.'
        : null,
      submittingOrder: false,
      storingCart: false,
    };
  }
  if (
    (state.retouchingCheckoutProtected || state.retouchingCheckoutRequest) &&
    (cartMutationActions.has(action.type) || action.type === GET_SHOOT_DATA) &&
    !(
      action.type === V3_CLEAR_CART &&
      action.payload?.verifiedUuid === state.uuid
    )
  )
    return state;
  const next = reduceOrder(state, action);
  if (action.type === V3_CLEAR_CART && next !== state)
    return {
      ...next,
      retouchingCheckoutProtected: false,
      retouchingCheckoutRequest: null,
      retouchingAttemptId: null,
      retouchingCheckoutMessage: null,
      retouchingQuoteDigest: null,
      retouchingCheckoutApplicable: null,
      retouchingQuoteReviewedGeneration: null,
      retouchingQuoteGeneration: (state.retouchingQuoteGeneration || 0) + 1,
    };
  if (
    action.type === 'V3_APPLY_RETOUCHING_QUOTE' ||
    retouchingOfferActions.has(action.type)
  )
    return next;
  const changed =
    (next !== state &&
      (next.orders !== state.orders ||
        next.discountCodes !== state.discountCodes ||
        cartMutationActions.has(action.type))) ||
    (quoteInputActions.has(action.type) && !action.error);
  return changed
    ? {
        ...next,
        retouchingQuoteDigest: null,
        retouchingCheckoutApplicable: null,
        retouchingQuoteReviewedGeneration: null,
        retouchingQuoteGeneration: (state.retouchingQuoteGeneration || 0) + 1,
      }
    : next;
}
